import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import useVehicleSingle from "../../data/useVehicleSingle";
import VehiclePartsCard from "../../components/vehicles/VehiclePartsCard";
import VehicleJobs from "../../components/vehicles/VehicleJobs";
import CreateJobs from "../../components/jobs/CreateJobs";
import Tabs from "../../components/shared/Tabs/Tabs";
import LeadDocuments from "../../components/leads/LeadDocuments";
import VehicleWorkflowCard from "../../components/vehicles/VehicleWorkflowCard";
import VehicleWorkflowPanel from "../../components/vehicles/VehicleWorkflowPanel";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import VehiclePricingMatrix from "../../components/vehicles/VehiclePricingMatrix";

import VehicleHeaderBar from "../../components/vehicles/VehicleHeaderBar";
import VehicleImagesCard from "../../components/vehicles/VehicleImagesCard";
import VehicleDetails from "../../components/vehicles/VehicleDetails";
import VehicleDismantlingCard from "../../components/vehicles/VehicleDismantlingCard";
import VehicleNotesTabs from "../../components/vehicles/VehicleNotesTabs";
import VehicleComplianceCard from "../../components/vehicles/VehicleComplianceCard";
import VehicleQuickActions from "../../components/vehicles/VehicleQuickActions";
import VehicleFinancialSummary from "../../components/vehicles/VehicleFinancialSummary";

export default function Vehicle() {
  const { t } = useTranslation("vehicles");
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const { vehicle, lead } = useVehicleSingle(vehicleId);
  const { softDelete } = useVehiclesMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers(t("Vehicle"));

  const createJobsTargetId = `vehicle-create-jobs-${vehicleId}`;
  const isTme = CheckFeatureFlag("TME");
  const [searchParams] = useSearchParams();

  // Allow deep links straight to a tab (e.g. vehicles/{id}?tab=pricing from the sourcing badge).
  const tabParamValues: Record<string, number> = {
    overview: 0,
    documents: 1,
    ...(isTme ? { pricing: 2 } : {}),
    parts: 3,
    ...(isTme ? { financial: 4 } : {}),
  };
  const initialTab = tabParamValues[searchParams.get("tab") ?? ""] ?? 0;

  const [viewType, setViewType] = useState(initialTab);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const viewTypes = [
    { value: 0, label: t("Overview") },
    { value: 1, label: t("Documents") },
    ...isTme ? [{ value: 2, label: t("Pricing") }] : [],
    { value: 3, label: t("Parts") },
    ...isTme ? [{ value: 4, label: t("Financial") }] : [],
  ];


  useEffect(() => {
    if (!vehicle.data?.data && !vehicle.isLoading) {
      setInfo({
        name: t("Vehicle not found"),
        desc: t("The vehicle you are looking for does not exist"),
      });
      return;
    }

    if (!vehicle.data?.data && vehicle.isLoading) {
      setInfo({
        name: "",
        desc: "",
      });
      return;
    }

    setInfo({
      name: `${vehicle.data?.data?.vrm} ${vehicle.data?.data?.combinedMake} ${vehicle.data?.data?.modelRangeDescription}`,
      desc: t("View and manage {{vrm}}", { vrm: vehicle.data?.data?.vrm }),
    });
  }, [vehicleId, vehicle.isLoading, t]);


  if (vehicle.isLoading || !vehicle.data?.data) {
    return (
      <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  const handleDeleteVehicle = () => {
    if (!vehicleId) return;

    softDelete.mutate(vehicleId, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        navigate("/vehicles");
      },
      onError: () => {
        deleteHandlers.onError();
      },
    });
  };

  const vehicleData = vehicle.data.data;
  const leadData = lead.data?.data ?? null;

  return (
    <div className="mt-8 flex flex-col gap-4">
      <VehicleHeaderBar vehicle={vehicleData} lead={leadData} />

      {isTme ? (
        <VehicleWorkflowPanel vehicleId={vehicleId!} />
      ) : (
        <VehicleWorkflowCard
          vehicle={vehicleData}
          createJobsTargetId={createJobsTargetId}
        />
      )}

      <Tabs viewType={viewType} viewTypes={viewTypes} setViewType={setViewType} />

      {
        {
          0: (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
              <div className="flex flex-col gap-4">
                <VehicleImagesCard lead={leadData} />
                <VehicleDetails
                  vehicle={vehicleData}
                  price={leadData?.priceWithVAT ?? null}
                  collectionQuestions
                  extendedActions
                />
              </div>

              <div className="flex flex-col gap-4">
                <VehicleDismantlingCard vehicle={vehicleData} />
                <VehicleNotesTabs vehicle={vehicleData} lead={leadData} />
                <VehicleComplianceCard vehicle={vehicleData} lead={leadData} />
              </div>

              <div className="flex flex-col gap-4">
                <VehicleQuickActions vehicle={vehicleData} onCreateDismantleJob={() => setViewType(3)} />
              </div>
            </div>
          ),
          1: leadData ? (
            <LeadDocuments lead={leadData} droppedFiles={undefined} setDroppedFiles={undefined} />
          ) : (
            <div className="p-4 bg-white rounded-lg shadow text-gray-600">
              {t("No lead is associated with this vehicle, so there are no documents to display.")}
            </div>
          ),
          2: <VehiclePricingMatrix vehicleId={vehicleId} />,
          3: (
            <div className="flex flex-col gap-4">
              <VehiclePartsCard vehicle={vehicleData} />

              <VehicleJobs vehicle={vehicleData} />

              <div id={createJobsTargetId}>
                <CreateJobs lead={null} vehicle={vehicleData} />
              </div>
            </div>
          ),
          4: (
            <div className="rounded-lg bg-white p-4 shadow sm:p-6">
              <VehicleFinancialSummary vehicle={vehicleData} lead={leadData} />
            </div>
          ),
        }[viewType]
      }

      <div className="mt-6 flex justify-end">
        <ButtonInput
          label={t("Delete Vehicle")}
          isSubmit={false}
          onClick={() => setDeleteOpen(true)}
          classes="!bg-red-600 hover:!bg-red-700"
        />
      </div>

      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleDeleteVehicle}
        isLoading={softDelete.isLoading}
        title={t("Delete this vehicle?")}
        message={t("This will soft-delete the vehicle. It will not appear in normal vehicle results unless deleted filters are explicitly included.")}
        confirmButtonText={t("Delete Vehicle")}
      />
    </div>
  );
}
